import * as THREE from 'three/webgpu';
import { Simulation, SIM_DT } from '../sim/sim';
import type { Assists, CarSpec, ControlInput } from '../sim/types';
import { buildStage } from '../world/build';
import type { BuiltStage, StageSpec } from '../world/types';
import { WorldView, normalizeShadowCasters } from '../render/world-view';
import { CarView } from '../render/car-view';
import { CameraRig } from '../render/cameras';
import { capture, copySnapshot, lerpSnapshot, makeSnapshot } from '../render/interp';
import { InputSmoother } from '../input/smoothing';
import type { InputDevices } from '../input/devices';
import { loadCarModel } from '../render/car-model';
import { loadCodriver } from '../render/codriver-avatar';
import codriverUrl from '../assets/models/codriver.glb?url';
import type { GameTextures } from '../render/textures';
import { FramePipeline } from '../render/pipeline';
import { DynamicResolution, type QualitySettings } from '../render/quality';
import { ObstacleIndex, classifyContact } from '../world/obstacles';
import type { HitInput } from '../audio/sfx-model';

/** Max sim steps per rendered frame; beyond this, time is dropped (slow-motion, not a spiral). */
export const MAX_SUBSTEPS = 12;

export interface SessionOptions {
  renderer: THREE.WebGPURenderer;
  car: CarSpec;
  stage: StageSpec;
  assists: Assists;
  devices: InputDevices;
  seed?: number;
  /** URL of the car GLB (optional: a placeholder is used until/unless it loads). */
  modelUrl?: string | null;
  /** Ground/foliage textures (null: flat-colour fallback, e.g. the test pad). */
  textures: GameTextures | null;
  quality: QualitySettings;
}

/**
 * One drive on one stage: owns the simulation, the views and the fixed-step loop.
 * The loop samples input every sim step, caps substeps per frame and interpolates rendering.
 */
export class DriveSession {
  readonly sim: Simulation;
  readonly stage: BuiltStage;
  readonly world: WorldView;
  readonly car: CarView;
  readonly rig: CameraRig;
  readonly smoother = new InputSmoother();
  readonly devices: InputDevices;
  private readonly renderer: THREE.WebGPURenderer;
  private prev = makeSnapshot();
  private curr = makeSnapshot();
  private view = makeSnapshot();
  private acc = 0;
  paused = false;
  /** Paused: last redraw (performance.now()) and a pending forced redraw (resize, quality). */
  private pausedDrawAt = -1e9;
  private redraw = true;
  /** Optional input override (bots / benchmark). */
  autopilot: ((dt: number) => ControlInput) | null = null;
  private readonly focus = new THREE.Vector3();
  headlights = false;
  unitsMph = false;
  stepsLastFrame = 0;
  /** Largest chassis contact force over this frame's sim steps (N), for impact sounds. */
  impactLastFrame = 0;
  /** World direction of that strongest contact force (unit; for cosmetic damage). */
  readonly impactDir = new THREE.Vector3();
  /** The stage's tagged obstacles (trees, rocks, props), to tell what the car hit. */
  readonly obstacles: ObstacleIndex;
  private readonly hit: HitInput = { force: 0, kind: 'ground', left: 0, forward: 0 };
  simMsLastFrame = 0;
  /** CPU time of the whole frame() call, and of the render submission inside it (ms). */
  frameMsLastFrame = 0;
  renderMsLastFrame = 0;
  /** Sim clock (s): advances only while the sim runs. */
  simTime = 0;
  /** Car progress along the stage centreline (m) and lateral offset (m, + left); 0 on the pad. */
  progressS = 0;
  lateral = 0;
  private progressHint = -1;
  private readonly proj = { s: 0, d: 0, i: 0 };
  private lastMode = '';
  private lastShowcase = false;
  /** Called after reset-to-road with the new progress (co-driver re-sync, penalties). */
  onReset: ((s: number) => void) | null = null;
  pipeline: FramePipeline;
  quality: QualitySettings;
  readonly dynRes: DynamicResolution;
  /** Exhaust tip in car-local coordinates (from the model's mount empty). */
  private readonly exhaustLocal = new THREE.Vector3(-0.45, 0.32, -2.05);
  private readonly exhaustWorld = new THREE.Vector3();
  private readonly exhaustDir = new THREE.Vector3();

  private constructor(opts: SessionOptions, sim: Simulation, stage: BuiltStage) {
    this.renderer = opts.renderer;
    this.devices = opts.devices;
    this.sim = sim;
    this.stage = stage;
    this.obstacles = new ObstacleIndex(stage.colliders);
    this.quality = opts.quality;
    this.world = new WorldView(stage, opts.textures, opts.quality);
    this.car = new CarView(opts.car);
    this.world.scene.add(this.car.root);
    normalizeShadowCasters(this.car.root);
    const size = new THREE.Vector2();
    this.renderer.getSize(size);
    this.rig = new CameraRig(size.x / Math.max(1, size.y));
    this.dynRes = new DynamicResolution(opts.quality.renderScale);
    this.dynRes.enabled = opts.quality.dynamicResolution;
    this.pipeline = this.makePipeline(opts.quality);
    this.resetCarToSpawn();
  }

  static async create(opts: SessionOptions): Promise<DriveSession> {
    const stage = buildStage(opts.stage);
    const sim = await Simulation.create({ car: opts.car, colliders: stage.colliders, surfaces: stage.surfaces, assists: opts.assists, seed: opts.seed ?? 1 });
    const session = new DriveSession(opts, sim, stage);
    if (opts.modelUrl) {
      try {
        // A failed download is retried twice before the placeholder body is used.
        let model: Awaited<ReturnType<typeof loadCarModel>> | null = null;
        let last: unknown = null;
        for (let i = 0; i < 3 && !model; i++) {
          try {
            model = await loadCarModel(opts.modelUrl);
          } catch (e) {
            last = e;
            await new Promise((r) => setTimeout(r, 400 * (i + 1)));
          }
        }
        if (!model) throw last;
        session.car.setModel(model);
        const m = model.mounts;
        const t = (v?: THREE.Vector3): [number, number, number] | null => (v ? [v.x, v.y, v.z] : null);
        session.rig.mounts = {
          cockpit: t(m.cockpit_eye) ?? session.rig.mounts.cockpit,
          bonnet: m.bonnet_cam ? [m.bonnet_cam.x, m.bonnet_cam.y + 0.1, m.bonnet_cam.z + 0.25] : session.rig.mounts.bonnet,
          bumper: t(m.bumper_cam) ?? session.rig.mounts.bumper,
        };
        if (m.exhaust_tip) session.exhaustLocal.copy(m.exhaust_tip);
        // The co-driver figure (cockpit: his tablet carries the pace notes). Optional: the game runs without it.
        try {
          session.car.setCodriver(await loadCodriver(codriverUrl), m.codriver_seat ?? new THREE.Vector3(-0.36, 0.62, -0.58));
        } catch (e) {
          console.warn('[car] co-driver figure failed to load', e);
        }
        normalizeShadowCasters(session.car.root);
      } catch (e) {
        console.warn('[car] model failed to load; using the placeholder', e);
      }
    }
    return session;
  }

  /** Camera obstruction test against static world geometry (trees, banks, buildings). */
  private readonly cameraCollision = {
    heightAt: (x: number, z: number) => this.stage.heightAt(x, z),
    obstruction: (fx: number, fy: number, fz: number, tx: number, ty: number, tz: number) => {
      const dx = tx - fx;
      const dy = ty - fy;
      const dz = tz - fz;
      const len = Math.hypot(dx, dy, dz);
      if (len < 1e-3) return 1;
      return this.sim.castStatic(fx, fy, fz, dx / len, dy / len, dz / len, len) / len;
    },
  };

  /** Put the car back on the road at the nearest valid point, facing the direction of travel. */
  resetToRoad(): void {
    const v = this.sim.vehicle;
    const t = this.stage.track;
    if (!t || !this.stage.index) {
      this.resetCarToSpawn();
      return;
    }
    v.readPose();
    const p = this.stage.index.project(v.pos.x, v.pos.z);
    const i = Math.max(0, Math.min(t.s.length - 1, Math.round(p.s)));
    const y = this.stage.heightAt(t.x[i], t.z[i]);
    v.placeAt(t.x[i], y + 0.35, t.z[i], Math.atan2(t.tx[i], t.tz[i]));
    this.smoother.reset();
    capture(this.curr, v);
    copySnapshot(this.prev, this.curr);
    this.rig.reset();
    this.pipeline?.cut();
    this.progressHint = i;
    this.updateProgress();
    this.world.effects.marks.reset();
    this.onReset?.(this.progressS);
  }

  /** Put the car on the centreline at distance `s` facing along the road (look-dev, benchmark). */
  placeAtDistance(s: number, lateral = 0): void {
    const t = this.stage.track;
    if (!t) return;
    const i = Math.max(0, Math.min(t.s.length - 1, Math.round(s)));
    const x = t.x[i] + t.tz[i] * lateral;
    const z = t.z[i] - t.tx[i] * lateral;
    const y = this.stage.visualHeightAt ? this.stage.visualHeightAt(x, z) : this.stage.heightAt(x, z);
    this.sim.vehicle.placeAt(x, y + 0.4, z, Math.atan2(t.tx[i], t.tz[i]));
    this.smoother.reset();
    capture(this.curr, this.sim.vehicle);
    copySnapshot(this.prev, this.curr);
    this.rig.reset();
    this.pipeline?.cut();
    this.progressHint = i;
    this.updateProgress();
    this.world.effects.reset();
    this.onReset?.(this.progressS);
  }

  /** Track the car's distance along the stage (windowed search around the previous index). */
  private updateProgress(): void {
    const idx = this.stage.index;
    if (!idx) return;
    idx.project(this.curr.px, this.curr.pz, this.progressHint, this.proj);
    // Re-acquire globally if the windowed search lost the car (e.g. after a long off).
    if (Math.abs(this.proj.d) > 40) idx.project(this.curr.px, this.curr.pz, -1, this.proj);
    this.progressHint = this.proj.i;
    this.progressS = this.proj.s;
    this.lateral = this.proj.d;
  }

  resetCarToSpawn(): void {
    const sp = this.stage.spawn;
    this.sim.vehicle.placeAt(sp.x, sp.y + 0.02, sp.z, sp.heading);
    this.smoother.reset();
    capture(this.curr, this.sim.vehicle);
    copySnapshot(this.prev, this.curr);
    this.rig.reset();
    this.pipeline?.cut();
    this.progressHint = -1;
    this.updateProgress();
  }

  resize(w: number, h: number): void {
    this.redraw = true;
    this.rig.camera.aspect = w / Math.max(1, h);
    this.rig.camera.updateProjectionMatrix();
    this.world.cameraChanged();
  }

  /**
   * This frame's strongest chassis contact for the effects (src/audio/sfx-model.ts): how hard, what
   * was hit and where on the car; null if nothing touched. The returned object is reused.
   */
  collision(): HitInput | null {
    const force = this.impactLastFrame;
    if (force <= 0) return null;
    const v = this.sim.vehicle;
    return classifyContact(this.obstacles, this.stage.heightAt, force, this.impactDir.y, v.pos.x, v.pos.z, v.fwd.x, v.fwd.z, this.hit);
  }

  /** Switch graphics preset at runtime (rebuilds the frame pipeline if AO changes). */
  setQuality(q: QualitySettings): void {
    this.quality = q;
    this.redraw = true;
    this.world.applyQuality(q);
    this.dynRes.max = q.renderScale;
    this.dynRes.scale = Math.min(this.dynRes.scale, q.renderScale);
    this.dynRes.enabled = q.dynamicResolution;
    const p = this.pipeline;
    if (q.ao !== p.aoEnabled || q.motionBlur !== p.motionBlurEnabled || q.dof !== p.dofEnabled) {
      p.dispose();
      this.pipeline = this.makePipeline(q);
    } else p.setScale(this.dynRes.scale);
  }

  private makePipeline(q: QualitySettings): FramePipeline {
    return new FramePipeline(this.renderer, this.world.scene, this.rig.camera, { ao: q.ao, motionBlur: q.motionBlur, dof: q.dof, scale: this.dynRes.scale });
  }

  /** Advance the simulation by real time dt (s) and render. */
  frame(dt: number): void {
    const tFrame = performance.now();
    const d = this.devices;
    d.poll();
    if (d.consumePressed('camera') && !this.rig.showcase) {
      this.rig.cycle();
      this.pipeline.cut();
    }
    const v = this.sim.vehicle;
    const t0 = performance.now();
    let steps = 0;
    this.impactLastFrame = 0;
    if (!this.paused) {
      this.acc += Math.min(dt, 0.25);
      while (this.acc >= SIM_DT && steps < MAX_SUBSTEPS) {
        const input = this.autopilot
          ? this.autopilot(SIM_DT)
          : this.smoother.update(d.raw, { speed: v.telemetry.forwardSpeed, slipAngle: v.telemetry.bodySlipAngle }, SIM_DT);
        copySnapshot(this.prev, this.curr);
        this.sim.step(input);
        if (this.sim.lastImpact > this.impactLastFrame) {
          this.impactLastFrame = this.sim.lastImpact;
          const d = this.sim.lastImpactDir;
          this.impactDir.set(d.x, d.y, d.z);
        }
        capture(this.curr, v);
        this.acc -= SIM_DT;
        steps++;
      }
      if (steps === MAX_SUBSTEPS) this.acc = 0;
      this.simTime += steps * SIM_DT;
      if (steps) this.updateProgress();
    }
    this.stepsLastFrame = steps;
    this.simMsLastFrame = performance.now() - t0;
    const alpha = this.paused ? 1 : this.acc / SIM_DT;
    lerpSnapshot(this.view, this.prev, this.curr, alpha);
    this.car.update(this.view);
    if (d.consumePressed('lights')) this.headlights = !this.headlights;
    this.car.animate(
      { steerAngle: v.steerAngle, handbrake: v.applied.handbrake, brake: v.telemetry.brake, gear: v.telemetry.gear, rpm: v.telemetry.rpm, speedKmh: v.telemetry.speed * 3.6, cockpit: this.rig.interior, headlights: this.headlights, unitsMph: this.unitsMph, now: performance.now(), gLat: v.telemetry.gLat, gLong: v.telemetry.gLong, gVert: v.telemetry.gVert, yawRate: v.telemetry.yawRate, speed: v.telemetry.speed, reduceMotion: this.rig.settings.reduceMotion },
      Math.min(dt, 0.1),
    );
    const t = v.telemetry;
    this.rig.update(
      this.view,
      { lookYaw: d.look.yaw, lookPitch: d.look.pitch, lookBack: d.look.back, gLong: t.gLong, gLat: t.gLat, gVert: t.gVert, speed: t.speed },
      Math.min(dt, 0.1),
      this.cameraCollision,
    );
    this.focus.set(this.view.px, this.view.py, this.view.pz);
    this.world.update(this.focus, this.rig.camera);
    // Effects: dust, spray, marks, anti-lag flames (exhaust from the interpolated car pose).
    if (!this.paused) {
      this.car.root.updateMatrixWorld();
      this.exhaustWorld.copy(this.exhaustLocal).applyMatrix4(this.car.root.matrixWorld);
      this.exhaustDir.set(0, 0, -1).transformDirection(this.car.root.matrixWorld);
      this.world.effects.update(v, Math.min(dt, 0.1), this.exhaustWorld, this.exhaustDir, this.world.lightLevel);
    }
    if (this.dynRes.sample(dt * 1000, Math.min(dt, 0.1))) this.pipeline.setScale(this.dynRes.scale);
    const tRender = performance.now();
    // Depth of field only on the showcase (the car in focus); motion blur only while driving.
    const pl = this.pipeline;
    if (pl.dofEnabled) {
      const target = this.rig.showcase ? 1.6 : 0;
      pl.bokeh.value += (target - pl.bokeh.value) * Math.min(1, dt * 3);
      pl.focus.value = this.rig.camera.position.distanceTo(this.focus);
    }
    if (this.rig.mode !== this.lastMode || this.rig.showcase !== this.lastShowcase) pl.cut();
    this.lastMode = this.rig.mode;
    this.lastShowcase = this.rig.showcase;
    // Paused, the picture behind the menu only changes with a setting: redraw a few times a second
    // (and at once after a resize or a quality change), not every frame. Each frame's render passes
    // leave garbage in the browser's heap that would use up the stage's GC headroom while the
    // player sits in the pause menu (src/game/gc-guard.ts).
    if (!this.paused || this.redraw || tRender - this.pausedDrawAt > 250) {
      const warming = this.world.warming;
      if (warming) this.world.warmFrame(true);
      pl.render(this.paused || this.rig.showcase ? 0 : 1);
      if (warming) this.world.warmFrame(false);
      this.redraw = false;
      if (this.paused) this.pausedDrawAt = tRender;
    }
    const tEnd = performance.now();
    this.renderMsLastFrame = tEnd - tRender;
    this.frameMsLastFrame = tEnd - tFrame;
  }
}
